<template>
  <Modal ref="modalRef" width="40rem" @after-close="emit('closed')">
    <template #body>
      <template v-if="!isApplicationSent">
        <View gap="xl" direction="column">
          <View gap="md" direction="column" align="center">
            <Text variant="h2">Send application to {{ renterFirstName }}</Text>
            <Text variant="body-large" color="secondary">
              The application will be sent to {{ renterFirstName }} at the contact information below:
            </Text>

            <UserCard :user="renter.user" size="medium" />
          </View>

          <Hint v-if="!hasToured" icon="info-circle">
            <View gap="sm" direction="column">
              <Text variant="h5">Heads up: {{ renterFirstName }} has not toured the property yet</Text>
              <Text variant="body-medium">
                Once the application is sent, Tallo will stop reaching out to schedule a showing
              </Text>
            </View>
          </Hint>

          <Hint gap="md">
            <template #icon>
              <IconTile icon="lightbulb-02" size="small" color="yellow" />
            </template>

            <View gap="md" direction="column">
              <Text variant="body-medium">
                Prompt decisions on applications provide the best experience for renters and ensure you don't miss out
                on potential tenants. You can always reconsider if you choose to pass on an application.
              </Text>
            </View>
          </Hint>

          <WhatToExpectCard :items="whatToExpectItems" />

          <!--
            The screening confirmations the send needs from the owner — TransUnion's attestations and terms, and
            under RentPrep its terms and the permissible-purpose certification — asked only when not on file for
            this person and property; whatever is on file is replayed by the backend. The form also says why it
            asks again for anything that lapsed, including while this was open.
          -->
          <ScreeningConfirmationsForm v-if="requirements" ref="confirmationsRef" :requirements="requirements" />
        </View>
      </template>

      <template v-else>
        <SendApplicationSuccess :renter-name="renterFirstName" />
      </template>
    </template>

    <template #footer>
      <View v-if="!isApplicationSent" gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button
          variant="primary"
          :disabled="!canSendApplication"
          :loading="isSendingApplication"
          @click="sendApplication"
        >
          Send application
        </Button>
      </View>

      <Button v-else variant="primary" @click="close">Done</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import {
  Button,
  Hint,
  IconTile,
  Modal,
  Text,
  View,
  serverErrorCode,
  useAlertsStore,
  WhatToExpectCard,
  WhatToExpectItem,
} from '@tallo/design-system';
import {
  applicationsService,
  PropertyAttestationsDto,
  ScreeningConfirmationsForm,
  sendConfirmationRequiredErrorCode,
} from '@tallo/investor/applications';
import { PropertyInquiryDto } from '@tallo/investor/property';
import { RentStage } from '@tallo/property';
import { RenterDto } from '@tallo/renter';
import { UserCard } from '@tallo/user';

import { useRenterProfileStore } from '../../store/renter-profile.store';
import SendApplicationSuccess from './send-application-success.vue';

const props = defineProps<{
  propertyInquiry: PropertyInquiryDto;
  renter: RenterDto;
  attestations: PropertyAttestationsDto | null;
  inquiryId?: string; // Optional inquiry ID for new leads
  loadBundles?: boolean; // Whether to load bundles after sending (default: true for renter profile, false for new leads)
}>();

const emit = defineEmits<{
  applicationSent: [];
  /** After it closes, sent or not: a panel that opened it can follow it out. */
  closed: [];
}>();

const alertsStore = useAlertsStore();
const renterProfileStore = useRenterProfileStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const confirmationsRef = ref<InstanceType<typeof ScreeningConfirmationsForm>>();
const isApplicationSent = ref(false);
const isSendingApplication = ref(false);

// The requirements the form works from: the caller's fetch, replaced by the modal's own refetch when a send
// comes back asking for a confirmation that lapsed meanwhile.
const requirements = ref<PropertyAttestationsDto | null>(props.attestations);

watch(
  () => props.attestations,
  (attestations) => (requirements.value = attestations),
);

const renterName = computed(() => props.renter.user.name);
const renterFirstName = computed(() => renterName.value.split(' ')[0]);

// Stages that mean the renter is at or past the showing. Deliberately an explicit
// list, not an enum-order comparison: SHOWING_CANCELLED_*, IDENTITY_VERIFICATION_FAILED
// and SHOWING_REQUEST_IGNORED_BY_OWNER all sit after SHOWING_COMPLETED in the enum but
// mean no tour happened.
const touredStages = [
  RentStage.SHOWING_COMPLETED,
  RentStage.APPLICATION_INVITE_SENT,
  RentStage.APPLICATION_IN_PROGRESS,
  RentStage.APPLICATION_COMPLETED,
  RentStage.APPLICATION_ACCEPTED,
  RentStage.APPLICATION_REJECTED,
];

const hasToured = computed(() => touredStages.includes(props.propertyInquiry.stage));

const canSendApplication = computed(() => confirmationsRef.value?.isComplete ?? false);

const whatToExpectItems = computed<WhatToExpectItem[]>(() => [
  {
    icon: 'link-01',
    title: `Tallo will send ${renterFirstName.value} an application link`,
    description: 'Tallo has partnered with accredited screening providers to securely handle the process.',
  },
  {
    icon: 'file-check-02',
    title: `Tallo will guide ${renterFirstName.value} through the process`,
    description: `There's no charge to you. ${renterFirstName.value} can invite co-applicants if necessary. Once completed by all parties, the application will be ready for your review.`,
  },
  {
    icon: 'folder',
    title: 'You will receive a complete application packet',
    description: `The packet includes credit, background, and eviction reports, along with ${renterFirstName.value}'s employment history, landlord references, and any other required documents.`,
  },
]);

function sendApplication() {
  isSendingApplication.value = true;

  // Only what the form showed: the backend replays whatever is on file for the property and this person.
  const requestData = confirmationsRef.value?.toRequestBody() ?? {};
  const targetInquiryId = props.inquiryId || props.propertyInquiry.id;
  const shouldLoadBundles = props.loadBundles ?? true;

  renterProfileStore
    .sendApplication(targetInquiryId, requestData, shouldLoadBundles)
    .then(() => {
      isApplicationSent.value = true;
      emit('applicationSent');
    })
    .catch((err) =>
      serverErrorCode(err) === sendConfirmationRequiredErrorCode ? askAgain() : alertsStore.catchError(err),
    )
    .finally(() => (isSendingApplication.value = false));
}

/**
 * A confirmation the send relied on lapsed between opening the modal and sending (the 90-day line crossed, or
 * TransUnion changed its attestations): fetch what is on file now and let the form ask for the rest and say why,
 * instead of a toast.
 */
async function askAgain() {
  try {
    requirements.value = await applicationsService.getPropertyAttestations(props.propertyInquiry.property.id);
  } catch (err) {
    alertsStore.catchError(err);
  }
}

function close() {
  modalRef.value?.close();
}

defineExpose({ modalRef });
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
